import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle, Lock } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenError, setTokenError] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Vérifier le token au chargement
    useEffect(() => {
        if (!token) {
            setTokenError('Lien invalide');
            setLoading(false);
            return;
        }
        
        const verifyToken = async () => {
            try {
                const res = await api.post('/auth/verify-reset-token', { token });
                if (res.data.valid) {
                    setTokenValid(true);
                    setMaskedEmail(res.data.email || '');
                } else {
                    setTokenError(res.data.reason || 'Lien invalide ou expiré');
                }
            } catch (err) {
                setTokenError('Lien invalide ou expiré');
            } finally {
                setLoading(false);
            }
        };
        
        verifyToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (newPassword !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }
        
        if (newPassword.length < 6) {
            toast.error('Le mot de passe doit contenir au moins 6 caractères');
            return;
        }
        
        setSubmitting(true);
        
        try {
            await api.post('/auth/reset-password', {
                token,
                new_password: newPassword
            });
            setSuccess(true);
            toast.success('Mot de passe réinitialisé !');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
                <Card className="w-full max-w-md">
                    <CardContent className="py-12 text-center">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
                        <p className="text-slate-600">Vérification du lien...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Token invalid
    if (!tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                            <XCircle className="w-8 h-8 text-red-600" />
                        </div>
                        <CardTitle className="text-2xl">Lien invalide</CardTitle>
                        <CardDescription>
                            {tokenError || 'Ce lien de réinitialisation est invalide ou a expiré.'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <Alert className="bg-amber-50 border-amber-200">
                            <AlertDescription className="text-amber-700 text-sm">
                                Les liens de réinitialisation ne sont valides que 60 minutes et ne peuvent être utilisés qu'une seule fois.
                            </AlertDescription>
                        </Alert>
                        <Link to="/forgot-password">
                            <Button className="w-full">
                                Demander un nouveau lien
                            </Button>
                        </Link>
                        <Link to="/login">
                            <Button variant="outline" className="w-full">
                                Retour à la connexion
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Success state
    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
                <Card className="w-full max-w-md">
                    <CardHeader className="text-center">
                        <div className="mx-auto mb-4 w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <CardTitle className="text-2xl">Mot de passe réinitialisé !</CardTitle>
                        <CardDescription>
                            Votre nouveau mot de passe a été enregistré. Vous pouvez maintenant vous connecter.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Button 
                            className="w-full" 
                            onClick={() => navigate('/login')}
                            data-testid="go-to-login-btn"
                        >
                            Se connecter
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Reset form
    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <div className="mx-auto mb-4 w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <Lock className="w-8 h-8 text-blue-600" />
                    </div>
                    <CardTitle className="text-2xl">Nouveau mot de passe</CardTitle>
                    <CardDescription>
                        {maskedEmail ? `Définissez un nouveau mot de passe pour ${maskedEmail}` : 'Définissez votre nouveau mot de passe'}
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="new-password">Nouveau mot de passe</Label>
                            <div className="relative">
                                <Input
                                    id="new-password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Minimum 6 caractères"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoFocus
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="confirm-password">Confirmer le mot de passe</Label>
                            <Input
                                id="confirm-password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Retapez le mot de passe"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                data-testid="confirm-password-input"
                            />
                        </div>
                        
                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                            <Alert variant="destructive">
                                <AlertDescription>Les mots de passe ne correspondent pas</AlertDescription>
                            </Alert>
                        )}
                        
                        <Button 
                            type="submit" 
                            className="w-full" 
                            disabled={submitting || newPassword.length < 6 || newPassword !== confirmPassword}
                            data-testid="reset-submit-btn"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Réinitialisation...
                                </>
                            ) : (
                                'Réinitialiser le mot de passe'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
